'use client';

/**
 * The AI review's empty-list rule: **a list with no items renders nothing**
 * (VALE-1737).
 *
 * ## The set
 *
 * Anything on an AI-extract review surface that can come back with no items is
 * a member, and the membership is derived rather than listed per surface:
 *
 * | Member | Where it renders |
 * |---|---|
 * | People | `CompactContactsReview` → `ContactList.emptyState` |
 * | Estate assets | `BalanceSheetSection` → `renderEmptyGroup` |
 * | Estate liabilities | `BalanceSheetSection` → `renderEmptyGroup` |
 * | Non-estate assets | `BalanceSheetSection` → `renderEmptyGroup` |
 * | Non-estate liabilities | `BalanceSheetSection` → `renderEmptyGroup` |
 * | Entitlements | `CompactEntitlementsReview` |
 * | Documents | `CompactDocumentsReview` |
 *
 * The non-estate pair renders only when that section is on screen at all, which
 * is its own rule (`BalanceSheet.showNonEstate`) and not this module's.
 *
 * ## What "nothing" means, and where the line is
 *
 * **The list's rows and its empty tile go; the heading goes with them unless it
 * still carries an Add.** That is the whole of the rule, and the second half is
 * load-bearing rather than a hedge.
 *
 * VALE-1657 gave all seven members one empty-state tile, and VALE-1494 before
 * it had argued the case for stating an empty group at all: *a group that
 * vanishes cannot be told apart from a group the review does not cover*. That
 * argument was right about the four balance-sheet groups, which stack inside
 * one card where a missing group closes the gap invisibly. It does not survive
 * the case VALE-1737 was opened on: an extract that legitimately finds nothing
 * in several categories renders a column of tiles restating the headings above
 * them, and the reviewer reads furniture instead of the data that was found.
 *
 * What the tile was really protecting is the **Add**. On the editable surfaces
 * (matter creation, the in-matter import's entitlements, client intake) an
 * empty list is still a list the reviewer can put something into, and hiding
 * it outright takes that affordance off the screen with no way back. So the
 * heading survives exactly as long as it has an action in it: every list the
 * reviewer can act on is still named, and only the ones they cannot are gone.
 *
 * **VALE-1756 tried the other way and it was reversed** (VALE-1924). It moved
 * every Add into one menu at the head of the review so an empty list could
 * draw nothing at all. On a populated review that separated the action from the
 * list it adds to: the reviewer had to go back to the top and pick the category
 * again rather than adding where they were already looking. Each Add is back in
 * its own list's heading, and the heading-with-an-Add clause came back with it.
 *
 * That clause is also what tells **"not covered"** from **"covered and found
 * empty"**: a category the review covers and can add to keeps its heading and
 * its Add over no rows, and one it does not cover draws nothing.
 *
 * A read-first surface (the enquiry Intake tab, the Title Search Review) has no
 * Add on any list, so nothing about an empty one is drawn. The Intake tab got
 * there by hand for its two lists (VALE-1699); this module is that answer
 * generalised, so the next surface does not re-derive it.
 *
 * ## How a member adopts it
 *
 * By rendering nothing for its empty case, which is what {@link
 * REVIEW_EMPTY_LIST_RENDERS_NOTHING} says and what every call site now does.
 * The constant exists so the decision is one edit rather than seven: it is a
 * judgement about the rendered page, and re-running it means looking at the
 * page again with the other value.
 */

/**
 * Whether an empty list on an AI review surface renders nothing at all.
 *
 * `true` — see this module's header for the argument, and
 * `.claude/docs/human-sign-off-protocol.md` for why it is a constant rather
 * than a prop: the answer was judged on a rendered screen, and the only honest
 * way to revisit it is to flip this and look again.
 */
export const REVIEW_EMPTY_LIST_RENDERS_NOTHING = true;

/**
 * What an empty AI review list renders.
 *
 * Kept as a component rather than deleted so the set above has one call site
 * per member and stays greppable — `ReviewEmptyState` is how a reader finds
 * every list that can come back empty, which is the question a change to this
 * rule has to answer first.
 */
export function ReviewEmptyState() {
  return REVIEW_EMPTY_LIST_RENDERS_NOTHING ? null : (
    /*
      The other branch is deliberately not reconstructed here. VALE-1657's tile
      — one icon, one `No <list>` title, no supporting line — is recoverable
      from that ticket's diff if the judgement is ever reversed; carrying a
      copy of it against a constant that is `true` would be a second definition
      of a treatment the surface does not draw.
    */
    <span data-review-empty-list="" hidden />
  );
}
